import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useMutation } from "@tanstack/react-query";
import api, { formatApiError } from "@/lib/api";
import { fmtTime, fmtDur, isoToNZInput, nzInputToISO } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Loader2, Trash2, HandHelping } from "lucide-react";

const REASONS = [
  "Travel between rooms", "Waiting on key", "Waiting on guest to leave",
  "Restocking trolley", "Helping elsewhere", "Break", "Other",
];

const isoToHm = (iso) => (iso ? isoToNZInput(iso).slice(11, 16) : "");
const hmToIso = (dateStr, hm) => (hm ? nzInputToISO(`${dateStr}T${hm}`) : null);

// Tag an idle gap with a reason. cleanerId omitted => backend defaults to the caller (a cleaner tagging their own gap).
// rooms (optional) enables a manager to recover a missed assist that was never recorded for this gap.
export function GapReasonDialog({ open, onClose, gap, date, cleanerId, cleanerName, rooms, onSaved }) {
  const { t } = useTranslation();
  const [reason, setReason] = useState("");
  const [note, setNote] = useState("");
  // assist recovery state
  const [showAssist, setShowAssist] = useState(false);
  const [aRoom, setARoom] = useState("");
  const [aStart, setAStart] = useState("");
  const [aEnd, setAEnd] = useState("");
  const [aReason, setAReason] = useState("");

  useEffect(() => {
    if (gap) {
      setReason(gap.reason || "");
      setNote(gap.note || "");
      setShowAssist(false);
      setARoom("");
      setAStart(isoToHm(gap.start));
      setAEnd(isoToHm(gap.end));
      setAReason("");
    }
  }, [gap]);

  const save = useMutation({
    mutationFn: (clear) =>
      api.post("/timeline/gap-note", {
        date,
        cleaner_id: cleanerId,
        start: gap.start,
        end: gap.end,
        reason: clear ? "" : reason,
        note: clear ? "" : note,
      }),
    onSuccess: (_r, clear) => {
      toast.success(clear ? t("Reason cleared") : t("Gap reason saved"));
      onSaved?.();
      onClose();
    },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });

  const addAssist = useMutation({
    mutationFn: () =>
      api.post(`/cleans/${aRoom}/assist/add-manual`, {
        cleaner_id: cleanerId,
        started_at: hmToIso(date, aStart),
        ended_at: hmToIso(date, aEnd),
        reason: aReason.trim() || undefined,
      }),
    onSuccess: () => {
      toast.success(t("Helper time added"));
      onSaved?.();
      onClose();
    },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });

  const canRecover = !!cleanerId && Array.isArray(rooms) && rooms.length > 0;
  const canAddAssist = aRoom && aStart && aEnd && aStart < aEnd && !addAssist.isPending;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="gap-reason-dialog">
        <DialogHeader>
          <DialogTitle>{t("Explain this idle gap")}</DialogTitle>
          <DialogDescription>
            {gap && (
              <>
                {fmtTime(gap.start)}–{fmtTime(gap.end)} · {fmtDur(gap.seconds)} {t("with no active task.")}
                {gap.by && <span className="mt-1 block text-xs">{t("Last set by {{name}}", { name: gap.by })}</span>}
              </>
            )}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label>{t("Reason")}</Label>
            <Select value={reason} onValueChange={setReason}>
              <SelectTrigger data-testid="gap-reason-select">
                <SelectValue placeholder={t("Choose a reason…")} />
              </SelectTrigger>
              <SelectContent>
                {REASONS.map((r) => (
                  <SelectItem key={r} value={r} data-testid={`gap-reason-opt-${r}`}>{t(r)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>{t("Note (optional)")}</Label>
            <Textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder={t("Any extra detail…")}
              maxLength={200}
              data-testid="gap-reason-note"
            />
          </div>

          {canRecover && (
            <div className="rounded-md border border-border bg-muted/30 p-3">
              {!showAssist ? (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 w-full justify-start gap-2 text-teal-700"
                  onClick={() => setShowAssist(true)}
                  data-testid="gap-add-assist-toggle"
                >
                  <HandHelping className="h-4 w-4" />
                  {t("Add missed helper time")}
                  {cleanerName ? ` — ${cleanerName}` : ""}
                </Button>
              ) : (
                <div className="space-y-2.5">
                  <div className="text-xs font-semibold text-muted-foreground">
                    {t("Record a helper session {{name}} did during this gap.", { name: cleanerName || "" })}
                  </div>
                  <div className="space-y-1.5">
                    <Label>{t("Room they helped on")}</Label>
                    <Select value={aRoom} onValueChange={setARoom}>
                      <SelectTrigger data-testid="gap-assist-room-select">
                        <SelectValue placeholder={t("Choose a room…")} />
                      </SelectTrigger>
                      <SelectContent>
                        {rooms.map((r) => (
                          <SelectItem key={r.clean_id} value={r.clean_id} data-testid={`gap-assist-room-${r.label}`}>
                            {r.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="space-y-1.5">
                      <Label>{t("Start")}</Label>
                      <Input type="time" value={aStart} onChange={(e) => setAStart(e.target.value)} data-testid="gap-assist-start" />
                    </div>
                    <div className="space-y-1.5">
                      <Label>{t("End")}</Label>
                      <Input type="time" value={aEnd} onChange={(e) => setAEnd(e.target.value)} data-testid="gap-assist-end" />
                    </div>
                  </div>
                  <Input
                    value={aReason}
                    onChange={(e) => setAReason(e.target.value)}
                    placeholder={t("Reason (optional)")}
                    maxLength={120}
                    data-testid="gap-assist-reason"
                  />
                  <div className="flex justify-end gap-2 pt-1">
                    <Button variant="ghost" size="sm" onClick={() => setShowAssist(false)} data-testid="gap-assist-cancel">
                      {t("Cancel")}
                    </Button>
                    <Button
                      size="sm"
                      className="gap-1.5 bg-teal-600 hover:bg-teal-700"
                      disabled={!canAddAssist}
                      onClick={() => addAssist.mutate()}
                      data-testid="gap-assist-save"
                    >
                      {addAssist.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
                      {t("Add helper time")}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
        <DialogFooter className="gap-2 sm:justify-between">
          {gap?.reason ? (
            <Button variant="outline" onClick={() => save.mutate(true)} disabled={save.isPending} data-testid="gap-reason-clear" className="gap-1.5 text-destructive">
              <Trash2 className="h-4 w-4" /> {t("Clear")}
            </Button>
          ) : <span />}
          <Button onClick={() => save.mutate(false)} disabled={save.isPending || !reason} data-testid="gap-reason-save" className="gap-2">
            {save.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
            {t("Save")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
